import { Injectable, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '../notification.service';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase.client';
import { AuthService } from '../auth/auth.service';
import { ErrorLoggerService } from '../error/error-logger.service';

interface PracticeMembersRow {
  practice_id: string;
  user_id: string;
  role_id: string;
  status: 'active' | 'invited' | 'removed';
}

interface PracticesRow {
  id: string;
  owner_id: string | null;
}

/**
 * Permissions live in the JWT (minted by the `custom_access_token_hook`),
 * so they are stale on open sessions until the token is refreshed. This
 * service listens for changes that can affect the signed-in user's effective
 * permissions and calls `supabase.auth.refreshSession()` to mint a new JWT.
 *
 * Two channels:
 *   - `practice_members` filtered by user_id — catches role reassignment,
 *     removal, and (via the `role_permissions_bump_members` trigger) edits
 *     to the user's current role.
 *   - `practices` filtered by id — catches ownership transfers.
 *
 * On removal we route the user to /practice-selection instead of just
 * refreshing, since they no longer have a role in the active practice.
 */
@Injectable({ providedIn: 'root' })
export class RealtimePermissionSyncService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly notify = inject(NotificationService);
  private readonly errorLogger = inject(ErrorLoggerService);

  private membersChannel: RealtimeChannel | null = null;
  private practiceChannel: RealtimeChannel | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const uid = this.auth.currentUserId();
      const pid = this.auth.practiceId();
      void this.resubscribe(uid, pid);
    });
  }

  private async resubscribe(uid: string | null, pid: string | null): Promise<void> {
    await this.teardown();
    if (!uid) return;

    this.membersChannel = supabase
      .channel(`perm-sync-members:${uid}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        { event: '*', schema: 'public', table: 'practice_members', filter: `user_id=eq.${uid}` },
        (payload: { new: PracticeMembersRow | null; old: PracticeMembersRow | null; eventType: string }) => {
          const next = payload.new;
          const activePid = this.auth.practiceId();
          if (
            next &&
            next.status === 'removed' &&
            activePid &&
            next.practice_id === activePid
          ) {
            void this.handleRemoval();
            return;
          }
          this.scheduleRefresh();
        },
      )
      .subscribe();

    if (pid) {
      this.practiceChannel = supabase
        .channel(`perm-sync-practice:${pid}`)
        .on(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          'postgres_changes' as any,
          { event: 'UPDATE', schema: 'public', table: 'practices', filter: `id=eq.${pid}` },
          (payload: { new: PracticesRow | null; old: PracticesRow | null }) => {
            if (payload.new?.owner_id !== payload.old?.owner_id) {
              this.scheduleRefresh();
            }
          },
        )
        .subscribe();
    }
  }

  private scheduleRefresh(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.refresh();
    }, 250);
  }

  private async refresh(): Promise<void> {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;
      const url = this.router.url;
      if (url) {
        await this.router.navigateByUrl(url, { skipLocationChange: true, onSameUrlNavigation: 'reload' });
      }
    } catch (error) {
      this.errorLogger.log(error, undefined, { context: 'RealtimePermissionSyncService.refresh' });
    }
  }

  private async handleRemoval(): Promise<void> {
    try {
      await this.auth.clearPractice();
      this.notify.info('You have been removed from this practice.', { duration: 4000 });
      await this.router.navigateByUrl('/practice-selection');
    } catch (error) {
      this.errorLogger.log(error, undefined, { context: 'RealtimePermissionSyncService.handleRemoval' });
    }
  }

  private async teardown(): Promise<void> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    const channels = [this.membersChannel, this.practiceChannel].filter(
      (c): c is RealtimeChannel => c !== null,
    );
    this.membersChannel = null;
    this.practiceChannel = null;
    for (const ch of channels) {
      try {
        await supabase.removeChannel(ch);
      } catch (error) {
        this.errorLogger.log(error, undefined, { context: 'RealtimePermissionSyncService.teardown' });
      }
    }
  }
}
